// Cadastro de Cliente / Hotel (issue #5). Base de Faturamento por Cliente
// (ADR-0002). A Rotina de Coleta e Devolução (Paradas) é agregado à parte (ADR-0014).

import { newId, type Id } from "./ids.ts";
import { type DadosParada, definirParadasDoCliente } from "./paradas.ts";
import { validarDiasVencimento } from "./vencimento.ts";
import { saldoBacklog } from "./backlog.ts";
import { loteEstaFaturado } from "./faturamento.ts";
import type {
  BaseFaturamento,
  CicloFechamento,
  Cliente,
  Snapshot,
} from "./snapshot.ts";

export type { Cliente };

export interface DadosCliente {
  nome: string;
  endereco: string;
  contato: string;
  codigo: string;
  tipo: string;
  baseFaturamento: BaseFaturamento;
  cicloFechamento: CicloFechamento;
  /** A devolução pode ser no mesmo dia da coleta? Padrão: não (ADR-0014). */
  devolucaoNoMesmoDia: boolean;
  /** Dias do mês de vencimento da Fatura, na ordem dos períodos do Ciclo (#40). */
  diasVencimento: number[];
  /** Paradas (Rotina de Coleta e Devolução) definidas no próprio cadastro (ADR-0014). */
  paradas?: DadosParada[];
  /** Tipos de Peça visíveis no lançamento de OS. `null` = sem restrição. */
  tiposPecaVisiveis?: string[] | null;
}

export class ErroCliente extends Error {}

const CODIGO = /^[A-Za-z]{3}$/;

function validar(
  dados: DadosCliente,
  snapshot: Snapshot,
  ignorarId?: Id,
): Omit<Cliente, "id" | "ativo" | "tiposPecaVisiveis"> {
  const nome = dados.nome.trim();
  if (nome === "") throw new ErroCliente("Informe o nome do cliente.");

  const codigo = dados.codigo.trim().toUpperCase();
  if (!CODIGO.test(codigo)) {
    throw new ErroCliente("O código deve ter exatamente 3 letras.");
  }
  if (
    snapshot.clientes.some((c) => c.id !== ignorarId && c.codigo === codigo)
  ) {
    throw new ErroCliente(`O código "${codigo}" já está em uso.`);
  }

  const ciclo = validarCiclo(dados.cicloFechamento);

  return {
    nome,
    endereco: dados.endereco.trim(),
    contato: dados.contato.trim(),
    codigo,
    tipo: dados.tipo.trim(),
    baseFaturamento: dados.baseFaturamento,
    cicloFechamento: ciclo,
    devolucaoNoMesmoDia: dados.devolucaoNoMesmoDia === true,
    diasVencimento: validarDiasVencimento(dados.diasVencimento ?? [], ciclo),
  };
}

function validarCiclo(ciclo: CicloFechamento): CicloFechamento {
  if (ciclo.tipo === "customizado") {
    const dentro = (d: number) => Number.isInteger(d) && d >= 1 && d <= 31;
    if (!dentro(ciclo.diaInicio) || !dentro(ciclo.diaFim)) {
      throw new ErroCliente("Dias do ciclo customizado devem ser de 1 a 31.");
    }
  }
  return ciclo;
}

export function criarCliente(
  snapshot: Snapshot,
  dados: DadosCliente,
  /** Id gerado no cliente (ADR-0003). Omitido = o domínio gera. */
  id: Id = newId(),
): Snapshot {
  const c: Cliente = {
    id,
    ...validar(dados, snapshot),
    tiposPecaVisiveis: dados.tiposPecaVisiveis !== undefined ? dados.tiposPecaVisiveis : null,
    ativo: true,
  };
  let out = { ...snapshot, clientes: [...snapshot.clientes, c] };
  if (dados.paradas) {
    out = definirParadasDoCliente(out, id, dados.paradas);
  }
  return out;
}

export function editarCliente(
  snapshot: Snapshot,
  id: Id,
  dados: DadosCliente,
): Snapshot {
  if (!snapshot.clientes.some((c) => c.id === id)) {
    throw new ErroCliente("Cliente não encontrado.");
  }
  const campos = validar(dados, snapshot, id);
  let out = {
    ...snapshot,
    clientes: snapshot.clientes.map((c) => {
      if (c.id === id) {
        const atualizado = { ...c, ...campos };
        if (dados.tiposPecaVisiveis !== undefined) {
          atualizado.tiposPecaVisiveis = dados.tiposPecaVisiveis;
        }
        return atualizado;
      }
      return c;
    }),
  };
  if (dados.paradas) {
    out = definirParadasDoCliente(out, id, dados.paradas);
  }
  return out;
}

export function definirAtivoCliente(
  snapshot: Snapshot,
  id: Id,
  ativo: boolean,
): Snapshot {
  if (!snapshot.clientes.some((c) => c.id === id)) {
    throw new ErroCliente("Cliente não encontrado.");
  }

  if (!ativo) {
    const temOsAberta = snapshot.lotes.some(
      (l) => l.clienteId === id && !l.canceladoEm && !loteEstaFaturado(snapshot, l.id),
    );
    if (temOsAberta) {
      throw new ErroCliente("Cliente possui OS(s) abertas. Fature ou cancele antes de inativar.");
    }
    const saldo = saldoBacklog(snapshot, id).reduce((acc, l) => acc + l.quantidade, 0);
    if (saldo > 0) {
      throw new ErroCliente(`Cliente possui saldo pendente de ${saldo} peça(s) no Backlog.`);
    }
  }

  return {
    ...snapshot,
    clientes: snapshot.clientes.map((c) => (c.id === id ? { ...c, ativo } : c)),
  };
}

export function descreveCiclo(ciclo: CicloFechamento): string {
  switch (ciclo.tipo) {
    case "mensal":
      return "Mensal";
    case "quinzenal":
      return "Quinzenal";
    case "customizado":
      return `Do dia ${ciclo.diaInicio} ao ${ciclo.diaFim}`;
  }
}